import { useEffect, useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import { MAX_SPECIFIC_USERS } from '@/lib/notifications/specific-users';
import { adminFetch } from '@/api';

export interface RecipientUser {
  id: string;
  name: string;
  email: string;
  hasPush: boolean;
}

const DEBOUNCE_MS = 250;
const MIN_QUERY = 2;

/**
 * Search users by name or email and pick several. Selected users show as
 * chips above the search box; tap a result again or the chip's × to remove.
 */
export function UserPicker({
  selected,
  onChange,
}: {
  selected: RecipientUser[];
  onChange: (users: RecipientUser[]) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<RecipientUser[]>([]);
  const [loading, setLoading] = useState(false);

  const trimmed = query.trim();
  useEffect(() => {
    if (trimmed.length < MIN_QUERY) return;
    let alive = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await adminFetch(
          `/api/notifications/recipients?search=${encodeURIComponent(trimmed)}`,
        );
        const data = res.ok
          ? ((await res.json()) as { users: RecipientUser[] })
          : { users: [] };
        if (alive) setResults(data.users);
      } finally {
        if (alive) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [trimmed]);

  const isSelected = (id: string) => selected.some(u => u.id === id);
  const atLimit = selected.length >= MAX_SPECIFIC_USERS;

  const toggle = (user: RecipientUser) => {
    if (isSelected(user.id)) {
      onChange(selected.filter(u => u.id !== user.id));
    } else if (!atLimit) {
      onChange([...selected, user]);
    }
  };

  const showResults = trimmed.length >= MIN_QUERY;
  const noPush = selected.filter(u => !u.hasPush).length;

  return (
    <div className="mt-2 space-y-2">
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map(u => (
            <span
              key={u.id}
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 pl-2.5 pr-1 py-0.5 text-[11.5px] text-white"
              title={u.email}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${u.hasPush ? 'bg-emerald-400' : 'bg-zinc-500'}`}
              />
              {u.name || u.email}
              <button
                type="button"
                onClick={() => toggle(u)}
                aria-label={`Remove ${u.name || u.email}`}
                className="rounded-full p-0.5 text-zinc-400 hover:bg-white/10 hover:text-white"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full rounded-lg border border-white/[0.06] bg-white/[0.04] py-2 pl-9 pr-3 text-[12.5px] text-white placeholder:text-zinc-600 focus:border-emerald-500/30 focus:outline-none"
        />
      </div>

      {showResults ? (
        <div className="max-h-56 overflow-y-auto rounded-lg border border-white/[0.06] bg-white/[0.02]">
          {loading && results.length === 0 ? (
            <div className="px-3 py-2 text-[11.5px] text-zinc-500">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-[11.5px] text-zinc-500">No users found</div>
          ) : (
            results.map(u => {
              const on = isSelected(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggle(u)}
                  disabled={!on && atLimit}
                  className={`flex w-full items-center gap-3 border-b border-white/[0.04] px-3 py-2 text-left last:border-b-0 transition-colors disabled:opacity-40 ${
                    on ? 'bg-emerald-500/5' : 'hover:bg-white/[0.03]'
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 flex-none items-center justify-center rounded border ${
                      on ? 'border-emerald-500 bg-emerald-500 text-black' : 'border-white/20'
                    }`}
                  >
                    {on ? <Check className="h-3 w-3" /> : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-medium text-white">
                      {u.name || '—'}
                    </span>
                    <span className="block truncate text-[10.5px] text-zinc-500">{u.email}</span>
                  </span>
                  <span
                    className={`text-[10px] font-medium ${u.hasPush ? 'text-emerald-400' : 'text-zinc-500'}`}
                  >
                    {u.hasPush ? 'push on' : 'no push'}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : (
        <div className="text-[10.5px] text-zinc-500">
          Type at least {MIN_QUERY} letters to search · up to {MAX_SPECIFIC_USERS} users
        </div>
      )}

      {noPush > 0 ? (
        <div className="text-[11px] text-amber-400/80">
          {noPush} selected {noPush === 1 ? 'user has' : 'users have'} no push token, so they
          won’t get a push on their phone.
        </div>
      ) : null}
    </div>
  );
}
